/*
 * A message's actions as mail clients draw them (Dawid, 2026-10-23: "reply and forward buttons are
 * icon buttons in the top right corners"): icon buttons at the end of the card's head, Reply, Reply
 * all, Forward, each named for assistive technology and, on a desktop, in an ink tip on hover and
 * keyboard focus. On a phone or touch screen there is no tip and each target is 44px (`styles.css`).
 * All three open the one composer (`reply-box.tsx`) with that message as its source.
 */
import { type ReactNode, useCallback, useState } from "react";
import { appDateTime } from "../../src/app-dates.ts";
import type { InboxMessage, InboxThread } from "../../src/inbox-v2/contract.ts";
import { ForwardArrowIcon } from "../icons.tsx";
import { Tip } from "../tip.tsx";

export const OPEN_FORWARD_MESSAGE = "leadcue-open-forward-message";
const selectedSources = new Map<string, InboxMessage>();

/** Keep the open source through responsive remounts and thread reads that omit it. */
export function useSelectedForward(userId: string, workspaceId: string, planId: string) {
  const key = [userId, workspaceId, planId].join("\u0000");
  const [source, setSource] = useState<InboxMessage | null>(() => selectedSources.get(key) ?? null);
  const choose = useCallback(
    (value: InboxMessage | null) => {
      if (value) selectedSources.set(key, value);
      else selectedSources.delete(key);
      setSource(value);
    },
    [key],
  );
  return [source, choose] as const;
}

/** Asks the composer to forward this message (`reply-box.tsx` listens). */
export function openForward(workspaceId: string, planId: string, messageId: string): void {
  window.dispatchEvent(
    new CustomEvent(OPEN_FORWARD_MESSAGE, { detail: { workspaceId, planId, messageId } }),
  );
}

/** One icon action: its name is the button's, the tip repeats it for the eye on a desktop. */
export function MailAction(props: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <span className="lcr-i2-mail-action">
      <button
        type="button"
        className="lcr-icon-button lcr-i2-mail-button"
        aria-label={props.label}
        disabled={props.disabled}
        onClick={props.onClick}
      >
        {props.icon}
      </button>
      <Tip className="lcr-i2-mail-tip">{props.label}</Tip>
    </span>
  );
}

/** The head's actions: Reply's owner supplies its buttons, Forward is drawn here where it is on. */
export function MessageActions(props: {
  workspaceId: string;
  planId: string;
  messageId: string;
  enabled: boolean;
  reply?: ReactNode;
}) {
  if (!props.reply && !props.enabled) return null;
  return (
    <div className="lcr-i2-mail-actions">
      {props.reply}
      {props.enabled && (
        <MailAction
          label="Forward"
          icon={<ForwardArrowIcon size={18} />}
          onClick={() => openForward(props.workspaceId, props.planId, props.messageId)}
        />
      )}
    </div>
  );
}

type HistoryEntry = NonNullable<InboxThread["forwards"]>[number];
const HISTORY_WORDS: Record<HistoryEntry["state"], string> = {
  sent: "Forwarded to",
  uncertain: "Forward unconfirmed to",
  cancelled: "Forward cancelled to",
  refused: "Forward not sent to",
  queued: "Forward pending to",
  claimed: "Forward pending to",
};

/** What became of this message's forwards: a quiet line each at the card's foot, never a control. */
export function ForwardHistory(props: {
  messageId: string;
  history?: InboxThread["forwards"] | undefined;
}) {
  const sends = props.history?.filter((send) => send.sourceMessageId === props.messageId) ?? [];
  if (sends.length === 0) return null;
  return (
    <ul className="lcr-i2-forward-history" aria-label="Forwards of this message">
      {sends.map((send) => (
        <li key={send.id}>
          <ForwardArrowIcon size={14} />
          <span>
            {HISTORY_WORDS[send.state]} {send.recipients.to.join(", ")}
            {send.recipients.cc.length ? ` · Cc: ${send.recipients.cc.join(", ")}` : ""}
            {send.recipients.bcc.length ? ` · Bcc: ${send.recipients.bcc.join(", ")}` : ""}
            {send.deliveredSubject ? ` · ${send.deliveredSubject}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Who wrote a message and when, as the composer names its source. */
export function sourceWords(message: Pick<InboxMessage, "from" | "at">): string {
  return `${message.from} · ${appDateTime(message.at)}`;
}
